import React, { useEffect, useRef, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import {
  EVENT_TYPES,
  EventType,
  PeakEvent,
  PeakEventInput,
  eventTypeLabel,
} from "../../types/event";
import BottomSheet from "../goals/BottomSheet";
import {
  FloatingInput,
  FloatingSelect,
  FloatingTextarea,
  floatingBorderFor,
  floatingLabelClass,
} from "../common/FloatingField";
import TagSelect from "../common/TagSelect";
import { fromInputDate, toInputDate } from "../../utils/goalDates";
import { dedupeTags } from "../../utils/eventTagUtils";

interface EventEditSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing athlete-authored event, or null to create. */
  event: PeakEvent | null;
  /** YYYYMMDD pre-filled when creating. */
  defaultDate?: string;
  /**
   * Tags to suggest: the union of tags on the athlete's own events and on
   * coach-issued events (no organisation library on the athlete side).
   */
  tagSuggestions?: string[];
  onSave: (input: PeakEventInput) => Promise<void> | void;
  /** Toggle `status: "withdrawn"`. */
  onWithdraw?: (event: PeakEvent, withdrawn: boolean) => Promise<void> | void;
  onDelete?: (event: PeakEvent) => Promise<void> | void;
}

/**
 * Create / edit an athlete-authored event (Events.md §3.2). This is the only
 * place athletes create or edit events; the inputs match the coach's event
 * form in PeakElite. Coach-issued events are never edited here.
 */
const EventEditSheet: React.FC<EventEditSheetProps> = ({
  isOpen,
  onClose,
  event,
  defaultDate,
  tagSuggestions = [],
  onSave,
  onWithdraw,
  onDelete,
}) => {
  const [name, setName] = useState("");
  const [type, setType] = useState<EventType>("competition");
  const [date, setDate] = useState("");
  // The end-date control is "activated" by tapping it; empty = single day.
  const [endActive, setEndActive] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(event?.name ?? "");
    setType(event?.type ?? "competition");
    setDate(event?.date ?? defaultDate ?? "");
    const hasEnd = !!event?.endDate && event.endDate !== event.date;
    setEndActive(hasEnd);
    setEndDate(hasEnd ? (event?.endDate ?? "") : "");
    setTags(dedupeTags(event?.tags));
    setNotes(event?.notes ?? "");
    setBusy(false);
    setConfirmDelete(false);
    setError(null);
  }, [isOpen, event, defaultDate]);

  // An empty end date is a single-day event; a set one must not precede start.
  const endInvalid = !!endDate && date.length === 8 && endDate < date;
  const canSave = name.trim().length > 0 && date.length === 8 && !endInvalid;

  const handleSave = async () => {
    if (!canSave) {
      setError(
        endInvalid
          ? "The end date can't be before the start date."
          : "A name and start date are required.",
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        type,
        date,
        // Single-day events carry no endDate.
        endDate: endDate && endDate > date ? endDate : undefined,
        notes: notes.trim() || undefined,
        // Empty list: omitted on create, cleared on update.
        tags: dedupeTags(tags),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn: () => Promise<void> | void) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const withdrawn = event?.status === "withdrawn";

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={event ? "Edit event" : "Add event"}
      busy={busy}
      wide
      footer={
        <>
          {error && <p className="text-sm text-error font-grotesk">{error}</p>}
          {event && (
            <div className="flex gap-2">
              {onWithdraw && (
                <button
                  type="button"
                  onClick={() => run(() => onWithdraw(event, !withdrawn))}
                  disabled={busy}
                  className="btn btn-outline flex-1 min-h-11 h-auto py-3 rounded-xl font-grotesk font-medium normal-case disabled:opacity-50"
                >
                  {withdrawn ? "Reinstate" : "Withdraw"}
                </button>
              )}
              {onDelete &&
                (confirmDelete ? (
                  <button
                    type="button"
                    onClick={() => run(() => onDelete(event))}
                    disabled={busy}
                    className="btn btn-error flex-1 min-h-11 h-auto py-3 rounded-xl font-grotesk font-semibold normal-case disabled:opacity-50"
                  >
                    Confirm delete
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    disabled={busy}
                    className="btn btn-outline btn-error flex-1 min-h-11 h-auto py-3 rounded-xl font-grotesk font-medium normal-case disabled:opacity-50"
                  >
                    Delete
                  </button>
                ))}
            </div>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !canSave}
            className="btn btn-primary w-full min-h-11 h-auto py-3 rounded-xl font-grotesk font-semibold normal-case disabled:opacity-50"
          >
            {busy ? "Saving..." : "Done"}
          </button>
        </>
      }
    >
      <FloatingInput
        id="event-name"
        label="Name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. National Championships"
        disabled={busy}
      />

      <FloatingSelect
        id="event-type"
        label="Type"
        value={type}
        onChange={(e) => setType(e.target.value as EventType)}
        disabled={busy}
      >
        {EVENT_TYPES.map((t) => (
          <option key={t} value={t}>
            {eventTypeLabel(t)}
          </option>
        ))}
      </FloatingSelect>

      <div className="grid grid-cols-2 gap-3">
        <FloatingInput
          id="event-date"
          label="Start date"
          type="date"
          value={toInputDate(date)}
          onChange={(e) => setDate(fromInputDate(e.target.value))}
          disabled={busy}
        />
        {endActive ? (
          <div className="relative">
            <FloatingInput
              id="event-end"
              inputRef={endRef}
              label="End date (optional)"
              type="date"
              value={toInputDate(endDate)}
              min={toInputDate(date) || undefined}
              onChange={(e) => setEndDate(fromInputDate(e.target.value))}
              onBlur={() => {
                // Left empty: fall back to a single-day event.
                if (!endDate) setEndActive(false);
              }}
              disabled={busy}
              error={endInvalid}
            />
            <button
              type="button"
              // mousedown (not click) so it runs before the input's blur.
              onMouseDown={(e) => {
                e.preventDefault();
                setEndDate("");
                setEndActive(false);
              }}
              disabled={busy}
              aria-label="Clear end date"
              className="absolute top-1 right-2 p-1 rounded text-base-content/50 hover:text-base-content hover:bg-base-content/10 transition-colors"
            >
              <HiXMark className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setEndActive(true);
              // Wait for the input to mount, then focus it.
              requestAnimationFrame(() => endRef.current?.focus());
            }}
            disabled={busy}
            aria-label="Add an end date for a multi-day event"
            className={`relative block w-full text-left border rounded-md bg-base-300/50 text-base sm:text-sm font-grotesk leading-5 pl-4 pr-4 pt-6 pb-2 opacity-60 hover:opacity-90 transition-opacity cursor-pointer focus:outline-none focus:ring-0 disabled:opacity-40 disabled:cursor-not-allowed ${floatingBorderFor(false)}`}
          >
            <span className="text-base-content/50 font-semibold">
              Single day
            </span>
            <span className={floatingLabelClass}>End date (optional)</span>
          </button>
        )}
      </div>
      {endInvalid && (
        <p className="-mt-2 text-xs text-error font-grotesk" role="alert">
          The end date can't be before the start date.
        </p>
      )}

      <TagSelect
        id="event-tags"
        label="Tags (optional)"
        value={tags}
        onChange={setTags}
        suggestions={tagSuggestions}
        disabled={busy}
      />

      <FloatingTextarea
        id="event-notes"
        label="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Qualifying standards, who to beat..."
        disabled={busy}
      />
    </BottomSheet>
  );
};

export default EventEditSheet;
